/**
 * Filtering a list — list engine v2's step filter and advanced builder.
 *
 * Two editors over one grammar, for two different moments.
 *
 * The **step filter** is the common case, and it is three stacked selectors
 * filled in order: which field, which condition, which value. Three steps
 * rather than a row of dropdowns because the second and third only make sense
 * once the first is chosen — an operator list is different for a date than for
 * a status, and offering all of them at once is offering nonsense.
 *
 * The **advanced builder** is for the query the step filter cannot say:
 * "completed, and (unbilled or big or old)". It reads its own definition back
 * in English at the bottom, and that sentence is rendered from the stored
 * filter rather than written alongside it — so it cannot describe something
 * the list is not actually doing.
 *
 * Both get their right-hand side from `filter-value.tsx`, so a status is a
 * dropdown and a date is a window in either one.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  FILTER_OPERATORS, NULLARY_OPERATORS, RELATIVE_UNITS, datePeriod, describeFilter,
  describePeriod, describeRelative, encodeView, isFilterGroup, isRelatedCondition,
  labelFor, relativeWindow, unseenText,
  type FilterCondition, type FilterGroup, type FilterNode, type FilterOperator,
  type RelatedCondition, type RelativeWindow,
} from '@rowted/shared'
import { Button, Segmented, SectionLabel } from './primitives.js'
import { Modal, Popover } from './overlays.js'
import { FilterValue, blankCondition, humanValue, valueReady } from './filter-value.js'
import { DateWindowButton } from './date-picker.js'
import { MenuMultiSelect, MenuSelect } from './menu-select.js'
import {
  COLUMN_ICONS, IconCheck, IconChevronDown, IconClose, IconCopy, IconGrip, IconGroup,
  IconLink, IconPlus, IconSave, IconSearch, IconSparkles,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import { optionLabel } from './filter-kind-controls.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

/** "3 fields", "1 row". */
const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

/** Operator labels that read as English rather than as an enum. */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  lt: 'is less than',
  lte: 'is at most',
  gt: 'is more than',
  gte: 'is at least',
  contains: 'contains',
  starts_with: 'starts with',
  in: 'is any of',
  not_in: 'is none of',
  is_null: 'is empty',
  is_not_null: 'is not empty',
  between: 'is between',
  in_period: 'is in',
  in_relative: 'is in',
}

/** Which operators make sense for a kind of column. */
export function operatorsFor(field: ListFieldDef): FilterOperator[] {
  switch (field.kind) {
    case 'text':
      return ['contains', 'starts_with', 'eq', 'neq', 'is_null', 'is_not_null']
    case 'number':
    case 'money':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null', 'is_not_null']
    case 'date':
    case 'datetime':
      /*
       * A window first. "Is in · last 30 days" is what anybody actually wants
       * from a date column, and it was reachable only by typing two dates into
       * `between` — which freezes into those two dates the moment the view is
       * saved.
       */
      return ['in_period', 'in_relative', 'gte', 'lte', 'between', 'is_null', 'is_not_null']
    case 'boolean':
      return ['eq']
    case 'enum':
      return ['in', 'not_in', 'eq', 'neq', 'is_null', 'is_not_null']
    case 'relation':
      /*
       * By identity first, by name second.
       *
       * This used to be identity or nothing, on the grounds that "contains"
       * against a uuid answers nothing — true of the *id*, but a relation has
       * a name too, and the server reads the label column for a text
       * operator. "Property is any of [two you picked]" and "Property
       * contains ridge" are both real questions, and the second is the only
       * one you can ask about rows you have not seen yet.
       */
      return ['in', 'not_in', 'contains', 'is_null', 'is_not_null']
    default:
      return [...FILTER_OPERATORS]
  }
}

/**
 * The operator list a *builder row* offers, which is not quite the grammar's.
 *
 * `in_relative` is one operator holding a direction, and the direction is the
 * thing somebody is choosing: "in the last 45 days" and "more than 45 days
 * ago" are different questions, not one question with a setting. So the row
 * offers them as three entries and folds the direction back into the value.
 *
 * Kept here rather than in the grammar because it is a fact about how the
 * choice reads, not about what can be stored.
 */
export type BuilderOp = FilterOperator | 'in_last' | 'in_next' | 'more_than_ago'

export const BUILDER_OP_LABELS: Record<string, string> = {
  ...OPERATOR_LABELS,
  in_last: 'in the last',
  in_next: 'in the next',
  more_than_ago: 'more than … ago',
}

const RELATIVE_DIRECTION: Record<string, RelativeWindow['direction']> = {
  in_last: 'past', in_next: 'future', more_than_ago: 'ago',
}

/** Which entry in the row's dropdown a stored condition is showing. */
export function builderOpOf(condition: FilterCondition): BuilderOp {
  if (condition.op !== 'in_relative') return condition.op
  const window = relativeWindow.safeParse(condition.value)
  if (!window.success) return 'in_last'
  return window.data.direction === 'future' ? 'in_next'
    : window.data.direction === 'ago' ? 'more_than_ago' : 'in_last'
}

/** That entry chosen, as a condition. */
export function withBuilderOp(
  condition: FilterCondition, chosen: BuilderOp, field: ListFieldDef | undefined,
): FilterCondition {
  const direction = RELATIVE_DIRECTION[chosen]
  if (direction) {
    const current = relativeWindow.safeParse(condition.value)
    const window: RelativeWindow = current.success
      ? { ...current.data, direction }
      : { unit: 'days', amount: 30, direction }
    return { field: condition.field, op: 'in_relative', value: window }
  }
  const op = chosen as FilterOperator
  return field ? blankCondition(field, op) : { ...condition, op }
}

/** The row's dropdown, in order, for a field. */
export function builderOpsFor(field: ListFieldDef | undefined): BuilderOp[] {
  if (!field) return [...FILTER_OPERATORS]
  const base = operatorsFor(field)
  return base.flatMap((op) => (op === 'in_relative'
    ? (['in_last', 'in_next', 'more_than_ago'] as BuilderOp[])
    : [op]))
}

/**
 * How a field and a value are written in the readback and on a chip.
 *
 * Exported so the card builder describes a card's filter in exactly the words
 * the filter chips use — the same condition should not read one way on a chip
 * and another way under a card.
 */
export function filterLabels(list: UseListResult) {
  return {
    field: (name: string) =>
      list.fields.find((candidate) => candidate.name === name)?.label ?? name,
    value: (value: unknown, condition: FilterCondition) => {
      if (condition.op === 'in_period') {
        const period = datePeriod.safeParse(condition.value)
        return period.success ? describePeriod(period.data) : 'a date window'
      }
      const field = list.fields.find((candidate) => candidate.name === condition.field)
      if (field?.kind === 'money') return `$${formatNumber(Number(value))}`
      if (field?.kind === 'boolean') return value === true || value === 'true' ? 'yes' : 'no'

      /*
       * **A filter never shows an id** (v3 §4.3).
       *
       * A relation's value is a uuid, and "Company is any of 4f2a…" is a
       * chip somebody can neither read nor act on. The name comes from the
       * condition's own `labels` — written when it was picked — or from
       * `labelCache`, which `useRelationLabels` fills for the ids a saved
       * view, a shared link or an older card carries without them.
       *
       * Three answers, and the third is the one worth being careful about.
       * A **name**: print it. **`null`**: looked up, and this person cannot
       * see that record — so say that rather than the id, because the filter
       * is still applying and a blank would read as a bug. **`undefined`**:
       * the lookup has not come back, so an ellipsis rather than a uuid that
       * will be replaced a moment later.
       */
      if (field?.kind === 'relation' || field?.kind === 'uuid') {
        const name = labelFor(condition, String(value), list.labelCache)
        if (name !== undefined) return name ?? lockText(field)
        return '\u2026'
      }
      return humanValue(value)
    },
    /*
     * The singular the area declares — "visit", not the relation key
     * "visits". Without this the sentence read "has no visits where …",
     * which is the plural doing the work of a quantity it does not have.
     */
    relation: (key: string) =>
      list.relations?.find((candidate) => candidate.key === key)?.label ?? key,
  }
}

/** `unseenText` for a field, which is how every caller here has one. */
export function lockText(field: { relation?: { label?: string } }): string {
  return unseenText(field.relation?.label)
}

/** Whether a column holds days, which is what the date picker is for. */
const isDateField = (field: ListFieldDef | undefined): boolean =>
  field?.kind === 'date' || field?.kind === 'datetime'

/*
 * The plain conditions at the top level — not groups, and not related
 * conditions, which carry another area's columns and would be read here as
 * this area's.
 */
const shallow = (group: FilterGroup | undefined): FilterCondition[] =>
  (group?.conditions ?? []).filter((node): node is FilterCondition =>
    !isFilterGroup(node) && !isRelatedCondition(node))


/**
 * A numbered step in a stacked form.
 *
 * Exported because the card builder's four sections are the same object — the
 * mockup draws "1 · WHAT TO MEASURE" exactly as this draws "1 · Filter by",
 * and two implementations of a numbered box is how two screens end up with
 * different-looking numbers on them.
 */
export function Step({
  n, label, done, children,
}: { n: number; label: string; done?: boolean; children: React.ReactNode }) {
  return (
    <div className={cn(
      'rounded-card border p-2',
      done ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface',
    )}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold',
          done ? 'bg-accent text-white' : 'bg-line-soft text-text-muted',
        )}>
          {n}
        </span>
        <SectionLabel>{label}</SectionLabel>
      </div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The advanced builder
// ---------------------------------------------------------------------------

/**
 * A draft without the rows nobody finished.
 *
 * The builder gains an empty row every time "Add condition" is clicked, so
 * applying it verbatim would filter on `is ""` and empty the table. A row
 * with no value is not a filter; it is a row somebody is still thinking
 * about. §4.3 is explicit that these are ignored rather than blocking: a
 * builder that refuses to apply because of a row you have not got to yet is
 * a builder that holds the other five conditions hostage.
 */
export function prune(group: FilterGroup): FilterGroup {
  const conditions = group.conditions
    .map((node) => (isFilterGroup(node) ? prune(node) : node))
    .filter((node) => {
      /*
       * A related condition with nothing in it still says something — "has a
       * visit" is a question — so it is kept whatever it holds.
       */
      if (isRelatedCondition(node)) return true
      return isFilterGroup(node)
        ? node.conditions.length > 0
        : valueReady(node, NULLARY_OPERATORS.includes(node.op))
    })
  return { ...group, conditions }
}

/** How many rows are being ignored, for the footer to own up to. */
export function incompleteCount(group: FilterGroup): number {
  return group.conditions.reduce((total, node) => {
    if (isRelatedCondition(node)) return total
    if (isFilterGroup(node)) return total + incompleteCount(node)
    return total + (valueReady(node, NULLARY_OPERATORS.includes(node.op)) ? 0 : 1)
  }, 0)
}

export function AdvancedFilter({
  list, open, onClose, value, onApply, title = 'Advanced filter', onSaveView,
}: {
  list: UseListResult
  open: boolean
  onClose: () => void
  /**
   * The filter being edited, when it is not the list's own.
   *
   * A card has a filter of its own — "which records this number counts" — and
   * it is the same grammar over the same columns, so it is the same builder.
   */
  value?: FilterGroup | null
  onApply?: (next: FilterGroup) => void
  title?: string
  /** Opens the save-view dialog carrying this filter. */
  onSaveView?: (filter: FilterGroup) => void
}) {
  const phone = usePhone()
  const labels = filterLabels(list)
  const filterable = list.fields.filter((field) => field.filterable)

  const opening = (): FilterGroup => {
    const current = onApply ? value : list.view.filter
    if (current && current.conditions.length > 0) return current
    const first = filterable[0]
    return {
      match: 'and',
      conditions: first ? [blankCondition(first, operatorsFor(first)[0]!)] : [],
    }
  }

  const [draft, setDraft] = useState<FilterGroup>(opening)
  const [tab, setTab] = useState<'builder' | 'text'>('builder')
  const [copied, setCopied] = useState(false)

  // Reopening shows what the list is actually filtered by, not what was left
  // in the box last time.
  useEffect(() => {
    if (open) { setDraft(opening()); setTab('builder'); setCopied(false) }
  }, [open])

  const [matches, setMatches] = useState<number | null>(null)
  const pruned = prune(draft)
  const ignored = incompleteCount(draft)

  /*
   * The live match count. It is the one thing that tells you a filter is
   * wrong before you apply it — "0 match" while you are still building has
   * saved you applying, seeing an empty table and undoing.
   */
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      list.countMatching(pruned)
        .then((count) => { if (!cancelled) setMatches(count) })
        .catch(() => { if (!cancelled) setMatches(null) })
    }, 300)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [JSON.stringify(pruned), open])

  const apply = () => {
    if (onApply) onApply(pruned)
    else list.setFilter(pruned.conditions)
    onClose()
  }

  /*
   * ⌘⏎ applies. A builder with six rows in it is a form, and every other
   * form in the product submits from the keyboard.
   */
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        apply()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, JSON.stringify(pruned)])

  const copyLink = () => {
    const url = new URL(window.location.href)
    url.searchParams.set('view', encodeView({ ...list.view, filter: pruned }))
    void navigator.clipboard?.writeText(url.toString())
    setCopied(true)
  }

  if (!open) return null

  return (
    <Modal
      open
      onClose={onClose}
      width={860}
      title={title}
      subtitle={`${humanValue(list.area)} · ${plural(filterable.length, 'field')}`}
      headerRight={
        /*
         * Text is the readback in a box, and read-only. A typed-filter parser
         * is a later PR; the segment is here now because leaving it out and
         * adding it later moves the close button, and a control that moves is
         * a control people stop trusting.
         */
        <Segmented
          size="large"
          label="How to edit this filter"
          value={tab}
          onChange={(next) => setTab(next as 'builder' | 'text')}
          options={[{ value: 'builder', label: 'Builder' }, { value: 'text', label: 'Text' }]}
        />
      }
      /*
        * The count goes in the note rather than beside the buttons.
        * `footerNote` is the footer's one shrinking slot; the buttons sit in
        * the `shrink-0` span next to it. With the count in there too, the row
        * could not shrink at all, and at 390px Apply went off the right edge
        * of the dialog — visible only on a phone, because that is the only
        * width where the row runs out of room.
        */
      footerNote={
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="flex items-center gap-1.5 text-sub text-text-muted">
            <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-accent" />
            {matches === null
              ? 'Counting…'
              : `${formatNumber(matches)} of ${formatNumber(list.totalCount)} match`}
          </span>
          {ignored > 0 && (
            <span className="text-sub text-text-faint">
              · {plural(ignored, 'row')} incomplete, ignored
            </span>
          )}
        </span>
      }
      footer={
        <>
          {/*
            * Five buttons in 860px; two in 390. Copy link and Clear are the
            * ones that go: a link copied on a phone has nowhere to be pasted,
            * and Clear is every row's × done four times. §4.5 splits the rest
            * across the width, 48px, so the two that matter are thumb-sized
            * rather than five that are not.
            */}
          <span className={cn('ml-auto flex items-center gap-2', phone && 'w-full')}>
            {!phone && (
              <Button onClick={copyLink} className="gap-1.5">
                <IconLink size={15} />
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            )}
            {onSaveView && (
              <Button
                size={phone ? 'big' : 'default'}
                onClick={() => onSaveView(pruned)}
                className={cn('gap-1.5', phone && 'flex-1 justify-center')}
              >
                <IconSave size={15} />
                Save as view
              </Button>
            )}
            {!phone && (
              <Button onClick={() => setDraft({ match: 'and', conditions: [] })}>Clear</Button>
            )}
            <Button
              variant="primary"
              size={phone ? 'big' : 'default'}
              onClick={apply}
              className={cn('gap-1.5', phone && 'flex-1 justify-center')}
            >
              <IconCheck size={15} />
              Apply
            </Button>
          </span>
        </>
      }
    >
      {tab === 'text'
        ? (
          <p className={cn('rounded-card border border-line bg-surface-alt p-3',
            'font-mono text-cell leading-relaxed text-text-body')}
          >
            {describeFilter(pruned, labels) || 'everything'}
          </p>
        )
        : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <SectionLabel>Match</SectionLabel>
              <Segmented
                size="large"
                label="How the top-level conditions combine"
                value={draft.match}
                onChange={(next) => setDraft({ ...draft, match: next as 'and' | 'or' })}
                options={[
                  { value: 'and', label: 'All of these' },
                  { value: 'or', label: 'Any of these' },
                ]}
              />
              <span className="text-sub text-text-faint">top level</span>
            </div>

            <GroupEditor
              list={list}
              group={draft}
              onChange={setDraft}
              depth={0}
              phone={phone}
            />
          </div>
        )}

      {/*
        * The sentence under the builder is the stored filter rendered, not a
        * second description of it. Two implementations would agree until
        * somebody added an operator, and then this would quietly stop
        * describing the query it sits under.
        */}
      <p className={cn('mt-3 flex gap-2 rounded-card border border-green-band-line',
        'bg-green-band-bg p-2.5 text-cell leading-normal text-green-band-text')}
      >
        <span aria-hidden className="pt-0.5"><IconSparkles size={13} /></span>
        <span><b>Reads as:</b> {describeFilter(pruned, labels) || 'everything'}.</span>
      </p>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// One group
// ---------------------------------------------------------------------------

function GroupEditor({
  list, group, onChange, depth, phone,
}: {
  list: UseListResult
  group: FilterGroup
  onChange: (next: FilterGroup) => void
  depth: number
  /**
   * One card per condition rather than one row (§4.5).
   *
   * The desktop row is five controls side by side in 860px. At 390 they
   * would each be 60px wide, so the row folds: the joiner and the two
   * actions on top, the field, the operator and the value stacked under it,
   * the whole thing in a bordered card so where one condition ends and the
   * next begins is still legible.
   */
  phone?: boolean
}) {
  const filterable = list.fields.filter((field) => field.filterable)
  const [dragging, setDragging] = useState<number | null>(null)

  const replace = (index: number, node: FilterNode | null) => {
    const conditions = node === null
      ? group.conditions.filter((_, i) => i !== index)
      : group.conditions.map((entry, i) => (i === index ? node : entry))
    onChange({ ...group, conditions })
  }

  const blank = (): FilterCondition | null => {
    const first = filterable[0]
    return first ? blankCondition(first, operatorsFor(first)[0]!) : null
  }

  const add = (node: FilterNode | null) => {
    if (!node) return
    onChange({ ...group, conditions: [...group.conditions, node] })
  }

  /** Move a row, which is what the grip is for. */
  const move = (from: number, to: number) => {
    if (from === to) return
    const next = [...group.conditions]
    const [row] = next.splice(from, 1)
    if (row) next.splice(to, 0, row)
    onChange({ ...group, conditions: next })
  }

  return (
    <div className={cn(depth > 0
      && 'rounded-card border border-dashed border-line-strong bg-surface-alt p-3')}
    >
      {depth > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <Segmented
            size="large"
            label="How these conditions combine"
            value={group.match}
            onChange={(next) => onChange({ ...group, match: next as 'and' | 'or' })}
            options={[
              { value: 'or', label: 'Any of these' },
              { value: 'and', label: 'All of these' },
            ]}
          />
        </div>
      )}

      <div className={cn('flex flex-col', phone && 'gap-2')}>
        {group.conditions.map((node, index) => {
          const short = !isFilterGroup(node) && !isRelatedCondition(node)
            && !valueReady(node, NULLARY_OPERATORS.includes(node.op))

            /*
              * Duplicate before remove. "The same but for the other branch"
              * is the commonest thing anybody does with a finished row, and
              * rebuilding it from the field picker is four clicks.
              */
            const actions = (
              <>
                <button
                  type="button"
                  aria-label="Duplicate this condition"
                  onClick={() => onChange({
                    ...group,
                    conditions: [
                      ...group.conditions.slice(0, index + 1),
                      structuredClone(node),
                      ...group.conditions.slice(index + 1),
                    ],
                  })}
                  className="shrink-0 rounded-field p-1 text-text-faint hover:bg-hover hover:text-text"
                >
                  <IconCopy size={13} />
                </button>
                <button
                  type="button"
                  aria-label="Remove this condition"
                  onClick={() => replace(index, null)}
                  className="shrink-0 rounded-field p-1 text-text-faint hover:bg-danger-bg hover:text-danger"
                >
                  <IconClose size={13} />
                </button>
              </>
            )

            /*
              * The joiner. The first row says "Where" and cannot be changed;
              * every row after it carries the and/or for the whole group,
              * because a group is one conjunction — mixing them inside one
              * level is what the nested group is for.
              */
            const joiner = (
              /*
               * 54px fitted "Where" and clipped the and/or dropdown it shares
               * the column with — a control whose own word is cut off is a
               * control nobody trusts. 78 is what "and" plus its chevron
               * needs, and the column stays one width so the rows line up.
               */
              <span className={cn('shrink-0 text-sub font-semibold text-text-muted',
                phone ? 'text-left' : 'w-[78px] text-right')}
              >
                {index === 0
                  ? 'Where'
                  : (
                    <MenuSelect
                      label="How these conditions combine"
                      value={group.match}
                      onChange={(next) => {
                        if (next) onChange({ ...group, match: next as 'and' | 'or' })
                      }}
                      options={[{ value: 'and', label: 'and' }, { value: 'or', label: 'or' }]}
                      minWidth={90}
                      size="small"
                      className={phone ? undefined : 'w-full'}
                    />
                  )}
              </span>
            )

            const editor = isFilterGroup(node)
              ? (
                <GroupEditor
                  list={list}
                  group={node}
                  onChange={(next) => replace(index, next)}
                  depth={depth + 1}
                  phone={phone}
                />
              )
              : isRelatedCondition(node)
                ? (
                  <RelatedRow
                    list={list}
                    node={node}
                    onChange={(next) => replace(index, next)}
                    phone={phone}
                  />
                )
                : (
                  <ConditionRow
                    list={list}
                    condition={node}
                    onChange={(next) => replace(index, next)}
                    phone={phone}
                  />
                )

            return (
              <div
                key={index}
                /*
                 * An unfinished row is marked, not faded.
                 *
                 * It used to sit at 55% opacity, which greyed the very
                 * controls somebody was reaching for — the row you are part
                 * way through being the hardest one to read is exactly
                 * backwards. A dashed rule down its left says "not counted
                 * yet" and leaves every word at full strength; the footer
                 * already says how many are being ignored.
                 */
                className={cn(
                  phone
                    ? cn('flex flex-col gap-2 rounded-card border p-3',
                      short ? 'border-dashed border-line-strong' : 'border-line')
                    : cn('flex items-center gap-2 border-l-2 py-1 pl-2',
                      /*
                       * The rule is always there, transparent when the row is
                       * finished, so marking one does not shunt it 10px right
                       * of the rows above it.
                       */
                      short ? 'border-dashed border-line-strong' : 'border-transparent'))}
                draggable={!phone && dragging === index}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => { if (dragging !== null) move(dragging, index); setDragging(null) }}
              >
                {phone
                  ? (
                    <div className="flex items-center gap-2">
                      {joiner}
                      <span className="ml-auto flex items-center gap-1">{actions}</span>
                    </div>
                  )
                  : (
                    <>
                      {/* Dragging is a pointer idea; a phone reorders by nothing. */}
                      <span
                        aria-hidden
                        onMouseDown={() => setDragging(index)}
                        onMouseUp={() => setDragging(null)}
                        className="shrink-0 cursor-grab text-line-strong"
                      >
                        <IconGrip size={14} />
                      </span>
                      {joiner}
                    </>
                  )}

                <div className={cn('flex min-w-0 flex-1 gap-2',
                  phone ? 'flex-col items-stretch' : 'items-center')}
                >
                  {editor}
                </div>

                {!phone && actions}
              </div>
            )
        })}
      </div>

      <div className={cn('mt-1.5 flex flex-wrap items-center gap-4',
        depth === 0 && !phone && 'ml-[100px]')}
      >
        <button
          type="button"
          onClick={() => add(blank())}
          className="flex items-center gap-1.5 text-cell font-semibold text-accent hover:text-accent-dark"
        >
          <IconPlus size={13} /> Add condition
        </button>

        {/* One level of nesting in the UI; the grammar itself has no limit. */}
        {depth === 0 && (
          <>
            <button
              type="button"
              onClick={() => {
                const first = blank()
                add({ match: 'or', conditions: first ? [first] : [] })
              }}
              className="flex items-center gap-1.5 text-cell font-semibold text-accent hover:text-accent-dark"
            >
              <IconGroup size={13} /> Add a group
            </button>

            {list.relations.length > 0 && (
              <MenuSelect
                label="Add a related-record condition"
                value={null}
                placeholder="Add a related-record condition"
                onChange={(relation) => {
                  if (relation) add({ relation, match: 'any', conditions: [] })
                }}
                options={list.relations.map((entry) => ({
                  value: entry.key, label: `has a ${entry.label} where…`,
                }))}
                minWidth={220}
                size="small"
                className="text-accent"
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// "has a visit where…"
// ---------------------------------------------------------------------------

/**
 * A nested mini builder over the *other* area's columns.
 *
 * The catalogue it needs is that area's, not this one's, and this component
 * only has this one — so the inner rows are drawn from what the condition
 * already names and edited as free text. Fetching the other area's fields to
 * draw a picker is worth doing and is not this PR; what matters here is that
 * a filter carrying one can be seen, inverted and removed rather than being
 * a chip nobody can open.
 */
function RelatedRow({
  list, node, onChange, phone,
}: {
  list: UseListResult
  node: RelatedCondition
  onChange: (next: RelatedCondition) => void
  /** Stacked rather than side by side (§4.5). */
  phone?: boolean
}) {
  const relation = list.relations.find((entry) => entry.key === node.relation)

  return (
    <div className={cn('flex min-w-0 flex-1 gap-2',
      phone ? 'flex-col items-stretch' : 'items-center')}
    >
      <MenuSelect
        label="Has or has no"
        value={node.match}
        onChange={(next) => { if (next) onChange({ ...node, match: next as 'any' | 'none' }) }}
        options={[
          { value: 'any', label: `has a ${relation?.label ?? node.relation}` },
          { value: 'none', label: `has no ${relation?.label ?? node.relation}` },
        ]}
        minWidth={200}
        className="w-[200px] shrink-0"
      />
      <span className="truncate text-cell text-text-muted">
        {node.conditions.length === 0
          ? 'of any kind'
          : `where ${plural(node.conditions.length, 'condition')}`}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One condition
// ---------------------------------------------------------------------------

function ConditionRow({
  list, condition, onChange, phone,
}: {
  list: UseListResult
  condition: FilterCondition
  onChange: (next: FilterCondition) => void
  /** Field, operator and value stacked rather than side by side (§4.5). */
  phone?: boolean
}) {
  const filterable = list.fields.filter((field) => field.filterable)
  const field = list.fields.find((candidate) => candidate.name === condition.field)
  const chosen = builderOpOf(condition)
  const nullary = NULLARY_OPERATORS.includes(condition.op)

  return (
    <div className={cn('flex min-w-0 flex-1 gap-2',
      phone ? 'flex-col items-stretch' : 'items-center')}
    >
      <FieldPicker
        fields={filterable}
        value={condition.field}
        onChange={(name) => {
          const next = list.fields.find((entry) => entry.name === name)
          if (!next) return
          const allowed = operatorsFor(next)
          const op = allowed.includes(condition.op) ? condition.op : allowed[0]!
          onChange(blankCondition(next, op))
        }}
      />

      <MenuSelect
        label="Condition"
        value={chosen}
        onChange={(next) => { if (next) onChange(withBuilderOp(condition, next as BuilderOp, field)) }}
        options={builderOpsFor(field).map((op) => ({
          value: op, label: BUILDER_OP_LABELS[op] ?? op,
        }))}
        minWidth={200}
        className={phone ? 'w-full' : 'w-[160px] shrink-0'}
      />

      {!nullary && (
        <span className={cn('flex min-w-0', phone ? 'w-full' : 'flex-1 basis-0')}>
          <BuilderValue
            list={list}
            field={field}
            condition={condition}
            onChange={onChange}
          />
        </span>
      )}
    </div>
  )
}

/**
 * The value half of a builder row.
 *
 * Not `KindControl`: that owns its operator — ticking a status chip there
 * decides `in` versus `not_in` — and here the operator is the dropdown to its
 * left. So this renders the value for a *given* operator, and the two share
 * the vocabulary (`summaryOf`, the chips, `DateField`) rather than the
 * component. Where they can be the same thing they are: an enum's chips and a
 * relation's search are the panel's, lifted in whole.
 */
function BuilderValue({
  list, field, condition, onChange,
}: {
  list: UseListResult
  field: ListFieldDef | undefined
  condition: FilterCondition
  onChange: (next: FilterCondition) => void
}) {
  if (condition.op === 'in_relative') {
    return <RelativeValue condition={condition} onChange={onChange} />
  }

  /*
   * **A date window is the picker, here too** (v3 §5.3).
   *
   * The builder used to carry its own: a seventeen-item dropdown, and two
   * bare date boxes behind a "These dates" option in it. That was the one
   * control in the app that could not show you what "last month" resolves
   * to, which is the whole question somebody opens a date filter to settle.
   *
   * The picker writes a whole condition, operator included — so choosing two
   * days on the calendar may turn `is between` into `is in`, and the operator
   * dropdown to the left re-renders to say so. That is the right way round:
   * the row stays a true sentence, and the alternative is a value cell
   * fighting the cell beside it.
   *
   * `in_relative` keeps `RelativeValue`, because the builder splits it into
   * three operators — *is in the last*, *is in the next*, *is more than N
   * ago* — and the dropdown has already said which. A picker whose count-back
   * row only writes "past" would be offering to contradict it.
   */
  if (isDateField(field) && (condition.op === 'in_period' || condition.op === 'between')) {
    return (
      <DateWindowButton
        list={list}
        label={field?.label ?? condition.field}
        field={condition.field}
        value={condition}
        onChange={(next) => onChange(next ?? blankCondition(field!, condition.op))}
      />
    )
  }

  /*
   * A picker only where the operator asks for one.
   *
   * "Is any of" and "is none of" name rows, so they get a list to tick. Every
   * other operator on the same field is a different question — "contains" is
   * about the name and wants a text box — and a row that showed the tick list
   * whatever the operator said was offering a list nobody could use.
   */
  if (field && (field.kind === 'enum' || field.kind === 'relation')
    && (condition.op === 'in' || condition.op === 'not_in')) {
    return <PickMany list={list} field={field} condition={condition} onChange={onChange} />
  }

  return (
    <FilterValue
      list={list}
      field={field}
      condition={condition}
      onChange={(value) => onChange({ ...condition, value })}
    />
  )
}

/**
 * "Is any of", as one line.
 *
 * The quick filter draws its values as an open box of chips, which is right
 * there: the panel is a column of sections and the chips are the section. A
 * builder row is a sentence — column, operator, value — and a box of eleven
 * statuses inside it pushes the next row off the bottom of the dialogue. Same
 * values, same words, one line: a dropdown of checkboxes that says "3
 * selected" when it is shut.
 *
 * An enum's values come from the list (what actually occurs, with counts); a
 * relation's come from the server as you type, because a page of properties
 * out of three thousand cannot be filtered in the browser.
 */
function PickMany({
  list, field, condition, onChange,
}: {
  list: UseListResult
  field: ListFieldDef
  condition: FilterCondition
  onChange: (next: FilterCondition) => void
}) {
  const relation = field.kind === 'relation'
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<{ value: string; label: string; sub?: string }[]>([])

  const picked = useMemo<string[]>(() => {
    const raw = Array.isArray(condition.value) ? condition.value : [condition.value]
    return raw.filter((one): one is string => typeof one === 'string')
  }, [condition.value])

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      const load = relation
        ? list.lookup(field.name, query).then((found) => found.map((row) => ({
          value: row.id,
          label: row.label,
          sub: row.count ? `${formatNumber(row.count)}` : undefined,
        })))
        : list.countedValues(field.name).then((found) => found.map((row) => ({
          value: String(row.value),
          /*
           * The field's own name for the value where it has one ("PA ·
           * Pennsylvania"), and the stored value read as words otherwise —
           * `in_progress` is "In progress" on a chip and must be the same
           * here, or one filter reads two ways in two menus.
           */
          label: field.options ? optionLabel(field, row.value) : humanValue(row.value),
          sub: formatNumber(row.count),
        })))
      void load
        .then((found) => { if (!cancelled) setRows(found) })
        .catch(() => { if (!cancelled) setRows([]) })
    }, query ? 180 : 0)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [field.name, query, relation, list])

  /*
   * Something ticked stays on the list even when the search no longer finds
   * it, or the trigger reads "2 selected" over a menu showing neither.
   */
  const options = [
    ...rows,
    ...picked
      .filter((value) => !rows.some((row) => row.value === value))
      .map((value) => ({
        value,
        label: (field.options ? optionLabel(field, value) : humanValue(value)) || value,
        sub: undefined as string | undefined,
      })),
  ].map((row) => ({
    value: row.value,
    label: row.label,
    detail: row.sub,
    search: `${row.label} ${row.value}`,
  }))

  return (
    <MenuMultiSelect
      label={field.label}
      values={picked}
      onChange={(next) => onChange({ ...condition, value: next })}
      options={options}
      placeholder={`Pick ${relation ? (field.relation?.label ?? 'one') : 'one'}…`}
      searchPlaceholder={relation ? `Find a ${field.relation?.label ?? 'row'}` : 'Find a value'}
      onQuery={relation ? setQuery : undefined}
      searchAlways={relation}
      className="min-w-0 flex-1"
      minWidth={260}
    />
  )
}

/** "in the last · 45 · days → Aug 12 – today". */
function RelativeValue({
  condition, onChange,
}: { condition: FilterCondition; onChange: (next: FilterCondition) => void }) {
  const parsed = relativeWindow.safeParse(condition.value)
  const window: RelativeWindow = parsed.success
    ? parsed.data
    : { unit: 'days', amount: 30, direction: 'past' }

  const write = (part: Partial<RelativeWindow>) =>
    onChange({ ...condition, value: { ...window, ...part } })

  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <label className={cn('flex h-[34px] w-[72px] shrink-0 items-center rounded-control',
        'border border-line bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent-ring')}
      >
        <span className="sr-only">How many</span>
        <input
          value={String(window.amount)}
          inputMode="numeric"
          onChange={(event) => write({ amount: Number(event.target.value.replace(/\D/g, '')) || 1 })}
          className="tnum w-full min-w-0 bg-transparent text-row font-semibold text-text focus:outline-none focus-visible:shadow-none"
        />
      </label>

      <MenuSelect
        label="Days, weeks or months"
        value={window.unit}
        onChange={(next) => { if (next) write({ unit: next as RelativeWindow['unit'] }) }}
        options={RELATIVE_UNITS.map((unit) => ({ value: unit, label: unit }))}
        minWidth={120}
        className="w-[104px] shrink-0"
      />

      {/*
        * The range it currently means, beside it. "45 days" is a number until
        * you can see it lands on August; a window whose meaning moves with
        * the calendar should say where it is today.
        */}
      <span className="truncate text-sub text-text-faint">
        → {describeRelative(window)}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// The field picker
// ---------------------------------------------------------------------------

/**
 * Every column, searchable, with its kind icon and its group.
 *
 * A `<select>` of thirty-two columns is a list you scroll looking for a word
 * you half remember. This is the same picker the quick filter's More fields
 * draws, for the same reason: the fastest way to find "Invoiced on" is to
 * type "inv".
 */
export function FieldPicker({
  fields, value, onChange, width = 200,
}: {
  fields: ListFieldDef[]
  value: string
  onChange: (name: string) => void
  width?: number
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const anchor = useRef<HTMLDivElement>(null)

  const chosen = fields.find((field) => field.name === value)
  const Icon = COLUMN_ICONS[chosen?.icon ?? 'text']

  const matches = fields.filter((field) =>
    field.label.toLowerCase().includes(query.trim().toLowerCase()))

  useEffect(() => { setCursor(0) }, [query])
  useEffect(() => { if (!open) setQuery('') }, [open])

  const pick = (name: string) => { onChange(name); setOpen(false) }

  return (
    <div ref={anchor} className="relative shrink-0" style={{ width }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Column"
        aria-expanded={open}
        className={cn('flex h-[34px] w-full items-center gap-2 rounded-control border px-2.5',
          'bg-surface text-body font-semibold text-text',
          open ? 'border-accent-line ring-2 ring-accent-ring' : 'border-line hover:bg-hover')}
      >
        <span aria-hidden className="shrink-0 text-text-faint"><Icon size={13} /></span>
        <span className="min-w-0 flex-1 truncate text-left">{chosen?.label ?? value}</span>
        <span aria-hidden className="shrink-0 text-text-faint"><IconChevronDown size={12} /></span>
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        label="Pick a column"
        width={300}
        anchor={anchor.current}
        className="rounded-popup"
      >
        <div className="p-2 pb-1">
          <label className={cn('flex h-[34px] items-center gap-2 rounded-control border',
            'border-line bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent-ring')}
          >
            <span aria-hidden className="shrink-0 text-text-muted"><IconSearch size={14} /></span>
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Find a column"
              placeholder="Find a column…"
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault()
                  setCursor((at) => Math.min(at + 1, matches.length - 1))
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault()
                  setCursor((at) => Math.max(at - 1, 0))
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  const target = matches[cursor]
                  if (target) pick(target.name)
                }
              }}
              className="w-full min-w-0 bg-transparent text-cell text-text placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
            />
          </label>
        </div>

        <div className="max-h-[320px] overflow-y-auto px-2 pb-1">
          {query && (
            <p className="px-2 pb-0.5 pt-1.5 text-label uppercase text-text-faint">
              Matches · {matches.length}
            </p>
          )}
          <ul className="flex list-none flex-col">
            {matches.map((field, index) => {
              const RowIcon = COLUMN_ICONS[field.icon]
              return (
                <li key={field.name}>
                  <button
                    type="button"
                    onClick={() => pick(field.name)}
                    onMouseEnter={() => setCursor(index)}
                    className={cn('flex h-8 w-full items-center gap-2 rounded-button px-2',
                      'text-left text-cell text-text',
                      index === cursor ? 'bg-accent-soft' : 'hover:bg-surface-alt')}
                  >
                    <span aria-hidden className="shrink-0 text-text-faint">
                      <RowIcon size={13} />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{field.label}</span>
                    {field.group && (
                      <span className="shrink-0 text-sub text-text-faint">{field.group}</span>
                    )}
                  </button>
                </li>
              )
            })}
            {matches.length === 0 && (
              <li className="px-2 py-3 text-center text-cell text-text-muted">
                No column matches that.
              </li>
            )}
          </ul>
        </div>

        <p className="border-t border-line-faint px-3 py-1.5 text-sub text-text-faint">
          ↑↓ move · ⏎ pick · every column is here
        </p>
      </Popover>
    </div>
  )
}

export { shallow as topLevelConditions }
